import type { ParsedDocument } from "@magic-md/core";
import { t as translate, type LocaleParams } from "@magic-md/i18n";
import type { StateCreator } from "zustand";
import {
    showConfirmDialog,
    showFilePickerDialog,
    showSaveDialog,
} from "../../components/dialogs";
import { isCompressedExternalJsonPath } from "../../lib/external-output-path";
import { isBinaryFile, isMarkdownFile } from "../../lib/file-type";
import {
    parseMagicMdDocumentAsync,
    parseMagicMdDocumentSync,
    shouldParseMagicMdOffThread,
} from "../../lib/markdown-parse-service";
import { fileNameFromPath } from "../../lib/path-utils";

/**
 * Parse content as Magic MD only when the tab points to a Markdown file.
 * For non-Markdown files (.json, .html, .ts, ...) the parsed AST is `null`,
 * which disables magic-syntax CodeMirror extensions and the DocumentRenderer
 * preview path; the file is treated as plain editable text instead.
 *
 * Untitled tabs (no filePath) are treated as Markdown to preserve the
 * "new document" UX.
 */
function parseMagicMd(content: string, filePath?: string): ParsedDocument | null {
    return parseMagicMdDocumentSync(content, filePath);
}

/**
 * Read a file's content as text — binary files (images, video, audio, PDF)
 * are skipped: their preview pulls bytes via `platform.getAssetUrl(filePath)`
 * instead, and storing utf8-mangled garbage in the editor would only confuse
 * users and Diff. Returns "" for binary kinds.
 */
async function readWorkspaceTextOrEmpty(filePath: string): Promise<string> {
    if (isBinaryFile(filePath) && !isCompressedExternalJsonPath(filePath)) {
        return "";
    }
    const { content } = await getPlatformService().engine.workspaceReadFile({
        path: filePath,
    });
    return content;
}
import { getPlatformService } from "../../platform";
import { frontendLog } from "../../utils/frontend-logger";
import { useRecentStore } from "../recent-store";
import { tabSessionRegistry } from "../tab-session-registry";
import type { EditorState, EditorTab, TabsSlice } from "./types";
import { getSidebarPresentation, newTabId, removePaneFromLayout } from "./utils";

function tr(key: string, params?: LocaleParams): string {
    return translate("editor", key, params);
}

/* ── Debounced parsing ── */
// Per-tab parse timers so that a content change in one tab doesn't cancel
// the pending parse for another tab (fixes cross-pane MCP write display bug).
const parseTimerMap = new Map<string, ReturnType<typeof setTimeout>>();
const parseRevisionMap = new Map<string, number>();
let nextParseRevision = 1;
const PARSE_DELAY = 150; // ms
const ACTIVATION_PARSE_DELAY = 0; // let tab selection paint before parsing uncached Markdown

function invalidateTabParse(tabId: string): number {
    const existingTimer = parseTimerMap.get(tabId);
    if (existingTimer) clearTimeout(existingTimer);
    parseTimerMap.delete(tabId);
    const revision = nextParseRevision++;
    parseRevisionMap.set(tabId, revision);
    return revision;
}

function discardTabParse(tabId: string): void {
    invalidateTabParse(tabId);
    parseRevisionMap.delete(tabId);
}

function commitTabParse(
    tabId: string,
    content: string,
    filePath: string | undefined,
    revision: number,
    parsed: ParsedDocument | null,
    get: () => EditorState,
    set: (partial: Partial<EditorState>) => void,
): void {
    if (parseRevisionMap.get(tabId) !== revision) return;
    const state = get();
    const tab = state.tabs.find((item) => item.id === tabId);
    if (!tab || tab.content !== content || tab.filePath !== filePath) return;

    const nextMap = new Map(state.parsedDocumentMap);
    nextMap.set(tabId, parsed);
    const update: Partial<EditorState> = { parsedDocumentMap: nextMap };
    if (tabId === state.activeTabId) update.parsedDocument = parsed;
    set(update);
}

function startTabParse(
    tabId: string,
    content: string,
    filePath: string | undefined,
    revision: number,
    get: () => EditorState,
    set: (partial: Partial<EditorState>) => void,
): void {
    if (!shouldParseMagicMdOffThread(content, filePath)) {
        commitTabParse(
            tabId,
            content,
            filePath,
            revision,
            parseMagicMd(content, filePath),
            get,
            set,
        );
        return;
    }

    void parseMagicMdDocumentAsync(content, filePath)
        .then((parsed) => {
            commitTabParse(
                tabId,
                content,
                filePath,
                revision,
                parsed,
                get,
                set,
            );
        })
        .catch((error) => {
            frontendLog.error("Failed to parse Markdown", error, {
                source: "system",
            });
        });
}

function scheduleTabParse(
    tabId: string,
    content: string,
    filePath: string | undefined,
    delay: number,
    get: () => EditorState,
    set: (partial: Partial<EditorState>) => void,
): void {
    const revision = invalidateTabParse(tabId);
    const timer = setTimeout(() => {
        parseTimerMap.delete(tabId);
        startTabParse(tabId, content, filePath, revision, get, set);
    }, delay);
    parseTimerMap.set(tabId, timer);
}

/* ── Guard against rapid-click duplicate file opens ── */
const _openingFiles = new Set<string>();

/* ── Debounced session autosave (.mdspace) ── */
let _sessionPersistTimer: ReturnType<typeof setTimeout> | null = null;
const SESSION_PERSIST_DELAY = 1500; // ms
export function scheduleSessionPersist(get: () => EditorState) {
    const state = get();
    if (state.isRestoringSession) return;
    if (!state.currentWorkspaceFile) return;
    if (_sessionPersistTimer) clearTimeout(_sessionPersistTimer);
    _sessionPersistTimer = setTimeout(() => {
        _sessionPersistTimer = null;
        try {
            void get().persistSessionState({ persist: true });
        } catch {
            /* swallow */
        }
    }, SESSION_PERSIST_DELAY);
}

export function scheduleTabActivationParse(
    tabId: string,
    get: () => EditorState,
    set: (partial: Partial<EditorState>) => void,
): void {
    const tab = get().tabs.find((item) => item.id === tabId);
    if (!tab) return;
    scheduleTabParse(
        tabId,
        tab.content,
        tab.filePath,
        ACTIVATION_PARSE_DELAY,
        get,
        set,
    );
}

/**
 * Tabs currently being migrated to another window.
 * When a tab is in this set, closing it will NOT trigger engine session cleanup,
 * allowing the destination window to reclaim the session by filePath.
 */
const _migratingTabIds = new Set<string>();

/** Check whether a tab is currently migrating to another window. */
export function isTabMigrating(tabId: string): boolean {
    return _migratingTabIds.has(tabId);
}

/* ── File change detection support ── */

/**
 * Flag to suppress file-change events right after we save a file ourselves.
 * Maps filePath → timestamp when we last wrote to it.
 */
const _recentSaves = new Map<string, number>();
const SAVE_COOLDOWN_MS = 2000; // ignore changes within 2s of our own save
const CONFIGS_PATH_MARKER = ".magic-md/configs/";
const CONFIG_FILE_CHANGED_EVENT = "magic-md:config-file-changed";
const BUNDLE_REFRESH_EVENT = "magic-md:bundle-refresh";

function isReadOnlyWorkspaceFile(filePath: string): boolean {
    return isBinaryFile(filePath) || isCompressedExternalJsonPath(filePath);
}

function isMdspaceFile(filePath: string): boolean {
    return filePath.toLowerCase().endsWith(".mdspace");
}

/**
 * Tracks files that currently have a confirm dialog open,
 * preventing duplicate dialogs from stacking.
 */
const _pendingConfirms = new Set<string>();

/* ── Pane layout helper (mirrors utils.removePaneFromLayout) ── */

export const createTabsSlice: StateCreator<EditorState, [], [], TabsSlice> = (
    set,
    get,
) => {
    /**
     * Reload one or more tabs that point to the same file path.
     * Updates parsedDocument for active tab if needed.
     */
    function _reloadTabsContent(
        filePath: string,
        tabIds: string[],
        showStatus: boolean,
    ): void {
        if (tabIds.length === 0) return;

        readWorkspaceTextOrEmpty(filePath)
            .then(async (newContent) => {
                const revisions = new Map(
                    tabIds.map((tabId) => [tabId, invalidateTabParse(tabId)]),
                );
                const parsed = await parseMagicMdDocumentAsync(
                    newContent,
                    filePath,
                );
                const s = get();
                const nextMap = new Map(s.parsedDocumentMap);
                const targetIds = new Set(
                    tabIds.filter((tabId) =>
                        parseRevisionMap.get(tabId) === revisions.get(tabId) &&
                        s.tabs.some((tab) =>
                            tab.id === tabId && tab.filePath === filePath,
                        ),
                    ),
                );
                if (targetIds.size === 0) return;
                const updatedTabs = s.tabs.map((t) => {
                    if (!targetIds.has(t.id)) return t;
                    nextMap.set(t.id, parsed);
                    if (t.content === newContent && !t.isDirty) return t;
                    return { ...t, content: newContent, isDirty: false };
                });

                const isActiveTarget = targetIds.has(s.activeTabId);
                set({
                    tabs: updatedTabs,
                    parsedDocumentMap: nextMap,
                    ...(isActiveTarget ? { parsedDocument: parsed } : {}),
                });

                if (showStatus) {
                    const firstTitle =
                        s.tabs.find((t) => targetIds.has(t.id))?.title ??
                        fileNameFromPath(filePath);
                    get().setStatusMessage(tr("tabs.status.autoReloaded", { title: firstTitle }));
                }
            })
            .catch(() => {
                get().setStatusMessage(tr("tabs.status.autoReloadFailed"));
            });
    }

    return {
        tabs: [],
        activeTabId: "",

        /* ── Content ── */
        setContent: (content: string, tabId?: string) => {
            const { activeTabId, tabs } = get();
            const targetTabId = tabId ?? activeTabId;
            const targetTab = tabs.find((t) => t.id === targetTabId);
            if (targetTab && targetTab.content === content && targetTab.isDirty) {
                return;
            }
            set((state) => ({
                tabs: state.tabs.map((t) =>
                    t.id === targetTabId ? { ...t, content, isDirty: true } : t,
                ),
            }));
            scheduleTabParse(
                targetTabId,
                content,
                targetTab?.filePath,
                PARSE_DELAY,
                get,
                set,
            );
        },

        setContentImmediate: (content: string, tabId?: string) => {
            const { activeTabId } = get();
            const targetTabId = tabId ?? activeTabId;
            const targetTab = get().tabs.find((t) => t.id === targetTabId);
            if (targetTab && targetTab.content === content && targetTab.isDirty) {
                return;
            }
            invalidateTabParse(targetTabId);
            const parsed = parseMagicMd(content, targetTab?.filePath);
            set((state) => {
                const nextMap = new Map(state.parsedDocumentMap);
                nextMap.set(targetTabId, parsed);
                const update: Record<string, unknown> = {
                    tabs: state.tabs.map((t) =>
                        t.id === targetTabId ? { ...t, content, isDirty: true } : t,
                    ),
                    parsedDocumentMap: nextMap,
                };
                if (targetTabId === state.activeTabId) {
                    update.parsedDocument = parsed;
                }
                return update as Partial<EditorState>;
            });
        },

        setExternalContent: (content: string, tabId: string) => {
            const targetTab = get().tabs.find((t) => t.id === tabId);
            if (!targetTab || targetTab.content === content) return;
            frontendLog.debug(
                `[content] setExternalContent: tab=${tabId} (${targetTab.title}) len=${content.length}`,
                undefined,
                { source: "engine" },
            );
            if (targetTab.filePath) {
                _recentSaves.set(targetTab.filePath, Date.now());
            }
            // Auto-save safety: setting `isDirty: false` cancels any pending
            // auto-save for this tab (auto-save bails out when !isDirty — see
            // the `options.source === "auto"` early-return in the save path),
            // which prevents stale local content from overwriting authoritative
            // content that just arrived from outside (engine sync, MCP write,
            // file watcher reload, Save As). DO NOT remove this.
            set((state) => ({
                tabs: state.tabs.map((t) =>
                    t.id === tabId ? { ...t, content, isDirty: false } : t,
                ),
            }));

            const isActive = tabId === get().activeTabId;
            if (isActive) {
                // Active tab: schedule a debounced parse on the same path
                // `setContent` uses. Multiple rapid `setExternalContent` calls
                // (e.g. streaming engine syncs) coalesce into a single parse.
                scheduleTabParse(
                    tabId,
                    content,
                    targetTab.filePath,
                    PARSE_DELAY,
                    get,
                    set,
                );
            } else {
                // Inactive tab: skip the eager parse entirely. Drop the stale
                // cached AST so `setActiveTab` lazily re-parses on the next
                // activation. This avoids burning CPU on tabs the user is not
                // looking at (and avoids N parses when many syncs stream in).
                invalidateTabParse(tabId);
                const cachedMap = get().parsedDocumentMap;
                if (cachedMap.has(tabId)) {
                    const nextMap = new Map(cachedMap);
                    nextMap.delete(tabId);
                    set({ parsedDocumentMap: nextMap });
                }
            }
        },

        /* ── Tabs ── */
        addTab: (partial) => {
            const id = partial?.id ?? newTabId();
            const tab: EditorTab = {
                id,
                title: partial?.title ?? "Untitled.md",
                filePath: partial?.filePath,
                sourceFilePath: partial?.sourceFilePath,
                content: partial?.content ?? "",
                isDirty: partial?.isDirty ?? false,
                kind: partial?.kind,
            };
            const offThread = shouldParseMagicMdOffThread(
                tab.content,
                tab.filePath,
            );
            const parsed = offThread
                ? null
                : parseMagicMd(tab.content, tab.filePath);
            set((s) => {
                const nextMap = new Map(s.parsedDocumentMap);
                nextMap.set(id, parsed);

                let nextPanes = s.panes;
                if (nextPanes.length > 0) {
                    nextPanes = nextPanes.map((p) =>
                        p.paneId === s.focusedPaneId
                            ? { ...p, tabId: id, tabIds: [...p.tabIds, id] }
                            : p,
                    );
                }

                return {
                    tabs: [...s.tabs, tab],
                    activeTabId: id,
                    parsedDocument: parsed,
                    parsedDocumentMap: nextMap,
                    panes: nextPanes,
                };
            });
            if (offThread) {
                scheduleTabParse(
                    id,
                    tab.content,
                    tab.filePath,
                    ACTIVATION_PARSE_DELAY,
                    get,
                    set,
                );
            }
        },

        closeTab: (id) => {
            discardTabParse(id);
            const { tabs, activeTabId } = get();
            const tab = tabs.find((t) => t.id === id);

            const doClose = () => {
                const idx = tabs.findIndex((t) => t.id === id);
                const next = tabs.filter((t) => t.id !== id);
                const nextMap = new Map(get().parsedDocumentMap);
                nextMap.delete(id);

                let nextPanes = get().panes.map((p) => {
                    const filteredIds = p.tabIds.filter((tid) => tid !== id);
                    const newActiveTab =
                        p.tabId === id
                            ? filteredIds.length > 0
                                ? filteredIds[
                                Math.min(p.tabIds.indexOf(id), filteredIds.length - 1)
                                ]
                                : ""
                            : p.tabId;
                    return { ...p, tabIds: filteredIds, tabId: newActiveTab };
                });
                const emptyPaneIds = nextPanes
                    .filter((p) => p.tabIds.length === 0)
                    .map((p) => p.paneId);
                let nextLayout = get().paneLayout;
                if (emptyPaneIds.length > 0 && nextPanes.length > emptyPaneIds.length) {
                    nextPanes = nextPanes.filter((p) => p.tabIds.length > 0);
                    if (nextLayout) {
                        for (const epId of emptyPaneIds) {
                            nextLayout = nextLayout
                                ? removePaneFromLayout(nextLayout, epId)
                                : null;
                        }
                    }
                } else if (
                    emptyPaneIds.length > 0 &&
                    nextPanes.length === emptyPaneIds.length
                ) {
                    nextPanes = [];
                    nextLayout = null;
                }
                if (nextPanes.length <= 1) {
                    nextPanes = [];
                    nextLayout = null;
                }

                if (next.length === 0) {
                    set({
                        tabs: [],
                        activeTabId: "",
                        parsedDocument: null,
                        parsedDocumentMap: nextMap,
                        panes: [],
                        paneLayout: null,
                    });
                    return;
                }
                const newActive =
                    id === activeTabId
                        ? next[Math.min(idx, next.length - 1)].id
                        : activeTabId;
                const hasCachedParsed = nextMap.has(newActive);
                const cachedParsed = hasCachedParsed ? nextMap.get(newActive) : null;
                const newActiveTab = next.find((t) => t.id === newActive);
                const shouldScheduleNewActiveParse =
                    !hasCachedParsed &&
                    !!newActiveTab &&
                    isMarkdownFile(newActiveTab.filePath);
                const parsed =
                    hasCachedParsed
                        ? (cachedParsed ?? null)
                        : shouldScheduleNewActiveParse
                            ? null
                            : parseMagicMd(
                                newActiveTab?.content ?? "",
                                newActiveTab?.filePath,
                            );
                if (!hasCachedParsed && !shouldScheduleNewActiveParse) {
                    nextMap.set(newActive, parsed);
                }

                const currentFocused = get().focusedPaneId;
                const newFocusedId =
                    nextPanes.length === 0
                        ? "pane-main"
                        : nextPanes.some((p) => p.paneId === currentFocused)
                            ? currentFocused
                            : nextPanes[0].paneId;

                set({
                    tabs: next,
                    activeTabId: newActive,
                    parsedDocument: parsed,
                    parsedDocumentMap: nextMap,
                    panes: nextPanes,
                    paneLayout: nextLayout,
                    focusedPaneId: newFocusedId,
                });
                if (shouldScheduleNewActiveParse) {
                    scheduleTabActivationParse(newActive, get, set);
                }
            };

            if (tab?.isDirty) {
                showConfirmDialog(`"${tab.title}" has unsaved changes. Close anyway?`, {
                    title: "Unsaved Changes",
                    destructive: true,
                }).then((ok) => {
                    if (ok) {
                        doClose();
                        scheduleSessionPersist(get);
                    }
                });
            } else {
                doClose();
                scheduleSessionPersist(get);
            }
        },

        markTabMigrating: (id) => {
            _migratingTabIds.add(id);
            setTimeout(() => _migratingTabIds.delete(id), 10_000);
        },

        setActiveTab: (id) => {
            const tab = get().tabs.find((t) => t.id === id);
            const { parsedDocumentMap, panes, focusedPaneId } = get();
            const hasCachedParsed = parsedDocumentMap.has(id);
            let parsed = hasCachedParsed ? (parsedDocumentMap.get(id) ?? null) : null;
            let nextParsedDocumentMap = parsedDocumentMap;
            let shouldScheduleActivationParse = false;
            if (!hasCachedParsed && tab) {
                if (isMarkdownFile(tab.filePath)) {
                    shouldScheduleActivationParse = true;
                } else {
                    parsed = parseMagicMd(tab.content, tab.filePath);
                    nextParsedDocumentMap = new Map(parsedDocumentMap);
                    nextParsedDocumentMap.set(id, parsed);
                }
            }

            let nextPanes = panes;
            if (panes.length > 0 && focusedPaneId) {
                nextPanes = panes.map((p) => {
                    if (p.paneId !== focusedPaneId) return p;
                    const tabIds = p.tabIds.includes(id) ? p.tabIds : [...p.tabIds, id];
                    return { ...p, tabId: id, tabIds };
                });
            }

            const update: Partial<EditorState> = {
                activeTabId: id,
                parsedDocument: parsed,
                panes: nextPanes,
                sidebarOpen:
                    get().sidebarOpen &&
                        getSidebarPresentation(get().sidebarView) === "content"
                        ? false
                        : get().sidebarOpen,
                selectedPluginName: null,
                selectedInstanceId: null,
                selectedSidecarId: null,
                selectedExtensionId: null,
                showEngineDetail: false,
                showMcpDetail: false,
                showSessionDetail: false,
            };
            if (nextParsedDocumentMap !== parsedDocumentMap) {
                update.parsedDocumentMap = nextParsedDocumentMap;
            }
            set(update);
            if (shouldScheduleActivationParse) {
                scheduleTabActivationParse(id, get, set);
            }
            scheduleSessionPersist(get);
        },

        getActiveTab: () => {
            const { tabs, activeTabId } = get();
            return tabs.find((t) => t.id === activeTabId);
        },

        reorderGlobalTabs: (tabIds: string[]) => {
            const { tabs } = get();
            const tabMap = new Map(tabs.map((t) => [t.id, t]));
            const reordered = tabIds
                .map((id) => tabMap.get(id))
                .filter(Boolean) as typeof tabs;
            for (const t of tabs) {
                if (!tabIds.includes(t.id)) reordered.push(t);
            }
            set({ tabs: reordered });
        },

        /* ── File operations ── */
        openFileFromPath: async (filePath: string) => {
            // .mdspace 文件默认作为工作区打开；当前工作区文件再次点击时进入可视化配置页。
            if (isMdspaceFile(filePath)) {
                const currentWorkspaceFile = get().currentWorkspaceFile;
                if (
                    currentWorkspaceFile &&
                    currentWorkspaceFile.toLowerCase() === filePath.toLowerCase()
                ) {
                    get().openWorkspaceConfig(filePath);
                    return;
                }
                await get().loadWorkspaceFromPath(filePath);
                return;
            }
            const existing = get().tabs.find((t) => t.filePath === filePath);
            if (existing) {
                get().setActiveTab(existing.id);
                return;
            }
            if (_openingFiles.has(filePath)) return;
            _openingFiles.add(filePath);

            const title = fileNameFromPath(filePath);
            const loadingTabId = newTabId();
            set((s) => {
                let nextPanes = s.panes;
                if (nextPanes.length > 0) {
                    nextPanes = nextPanes.map((p) =>
                        p.paneId === s.focusedPaneId
                            ? {
                                ...p,
                                tabId: loadingTabId,
                                tabIds: [...p.tabIds, loadingTabId],
                            }
                            : p,
                    );
                }
                return {
                    tabs: [
                        ...s.tabs,
                        {
                            id: loadingTabId,
                            title,
                            filePath,
                            content: "",
                            isDirty: false,
                            isLoading: true,
                        },
                    ],
                    activeTabId: loadingTabId,
                    parsedDocument: null,
                    panes: nextPanes,
                };
            });

            try {
                const content = await readWorkspaceTextOrEmpty(filePath);
                const revision = invalidateTabParse(loadingTabId);
                const parsed = await parseMagicMdDocumentAsync(content, filePath);
                if (
                    parseRevisionMap.get(loadingTabId) !== revision ||
                    !get().tabs.some((tab) =>
                        tab.id === loadingTabId && tab.filePath === filePath,
                    )
                ) {
                    return;
                }
                set((s) => {
                    const isActive = s.activeTabId === loadingTabId;
                    const nextMap = new Map(s.parsedDocumentMap);
                    nextMap.set(loadingTabId, parsed);
                    return {
                        tabs: s.tabs.map((t) =>
                            t.id === loadingTabId
                                ? { ...t, content, isDirty: false, isLoading: false }
                                : t,
                        ),
                        parsedDocument: isActive ? parsed : s.parsedDocument,
                        parsedDocumentMap: nextMap,
                    };
                });
                if (filePath) useRecentStore.getState().addRecentFile(filePath);
                get().setStatusMessage(tr("tabs.status.opened", { title }));
                scheduleSessionPersist(get);
            } catch (err) {
                frontendLog.error("Failed to open file", err, { source: "system" });
                set((s) => {
                    const next = s.tabs.filter((t) => t.id !== loadingTabId);
                    const nextMap = new Map(s.parsedDocumentMap);
                    nextMap.delete(loadingTabId);
                    const nextPanes = s.panes.map((p) => {
                        const filteredIds = p.tabIds.filter((tid) => tid !== loadingTabId);
                        const newActiveTab =
                            p.tabId === loadingTabId
                                ? filteredIds.length > 0
                                    ? filteredIds[filteredIds.length - 1]
                                    : ""
                                : p.tabId;
                        return { ...p, tabIds: filteredIds, tabId: newActiveTab };
                    });
                    const newActive =
                        s.activeTabId === loadingTabId
                            ? (next[next.length - 1]?.id ?? "")
                            : s.activeTabId;
                    return {
                        tabs: next,
                        activeTabId: newActive,
                        panes: nextPanes,
                        parsedDocument:
                            next.length > 0
                                ? (nextMap.get(newActive) ?? null)
                                : null,
                        parsedDocumentMap: nextMap,
                    };
                });
                const fallbackState = get();
                const fallbackTab = fallbackState.tabs.find(
                    (tab) => tab.id === fallbackState.activeTabId,
                );
                if (
                    fallbackTab &&
                    !fallbackState.parsedDocumentMap.has(fallbackTab.id)
                ) {
                    if (isMarkdownFile(fallbackTab.filePath)) {
                        scheduleTabActivationParse(fallbackTab.id, get, set);
                    } else {
                        const nextMap = new Map(fallbackState.parsedDocumentMap);
                        nextMap.set(fallbackTab.id, null);
                        set({ parsedDocumentMap: nextMap });
                    }
                }
                const errMsg = err instanceof Error ? err.message : String(err);
                if (
                    errMsg.includes("os error 60") ||
                    errMsg.includes("timed out") ||
                    errMsg.includes("iCloud")
                ) {
                    get().setStatusMessage(tr("tabs.status.icloudNotDownloaded"));
                } else {
                    get().setStatusMessage(tr("tabs.status.openFailed", { message: errMsg.slice(0, 80) }));
                }
            } finally {
                _openingFiles.delete(filePath);
            }
        },

        openWorkspaceConfig: (filePath) => {
            const workspaceFile = filePath ?? get().currentWorkspaceFile;
            if (!workspaceFile) {
                get().setStatusMessage(tr("tabs.status.noWorkspaceLoaded"), 3000);
                return;
            }
            const existing = get().tabs.find(
                (t) => t.kind === "workspace-config" &&
                    t.filePath?.toLowerCase() === workspaceFile.toLowerCase(),
            );
            if (existing) {
                get().setActiveTab(existing.id);
                return;
            }

            const id = newTabId();
            const title = fileNameFromPath(workspaceFile);
            set((s) => {
                const nextMap = new Map(s.parsedDocumentMap);
                nextMap.set(id, null);
                let nextPanes = s.panes;
                if (nextPanes.length > 0) {
                    nextPanes = nextPanes.map((p) =>
                        p.paneId === s.focusedPaneId
                            ? { ...p, tabId: id, tabIds: [...p.tabIds, id] }
                            : p,
                    );
                }
                return {
                    tabs: [
                        ...s.tabs,
                        {
                            id,
                            title,
                            filePath: workspaceFile,
                            content: "",
                            isDirty: false,
                            kind: "workspace-config",
                        },
                    ],
                    activeTabId: id,
                    parsedDocument: null,
                    parsedDocumentMap: nextMap,
                    panes: nextPanes,
                };
            });
            get().setStatusMessage(tr("tabs.status.openedWorkspaceConfig", { title }), 2000);
        },

        openDocumentDoctor: (filePath) => {
            const existing = get().tabs.find(
                (t) => t.kind === "document-doctor" && t.sourceFilePath === filePath,
            );
            if (existing) {
                get().setActiveTab(existing.id);
                return;
            }

            const id = newTabId();
            const title = `doctor · ${fileNameFromPath(filePath)}`;
            set((s) => {
                const nextMap = new Map(s.parsedDocumentMap);
                nextMap.set(id, null);
                let nextPanes = s.panes;
                if (nextPanes.length > 0) {
                    nextPanes = nextPanes.map((p) =>
                        p.paneId === s.focusedPaneId
                            ? { ...p, tabId: id, tabIds: [...p.tabIds, id] }
                            : p,
                    );
                }
                return {
                    tabs: [
                        ...s.tabs,
                        {
                            id,
                            title,
                            sourceFilePath: filePath,
                            content: "",
                            isDirty: false,
                            kind: "document-doctor",
                        },
                    ],
                    activeTabId: id,
                    parsedDocument: null,
                    parsedDocumentMap: nextMap,
                    panes: nextPanes,
                };
            });
            get().setStatusMessage(tr("tabs.status.openedDoctor", { name: fileNameFromPath(filePath) }), 2000);
        },

        openFile: async () => {
            const filePath = await showFilePickerDialog({});
            if (!filePath) return;

            if (isMdspaceFile(filePath)) {
                await get().openFileFromPath(filePath);
                return;
            }

            try {
                const content = await readWorkspaceTextOrEmpty(filePath);
                const title = fileNameFromPath(filePath);

                const active = get().getActiveTab();
                if (active && !active.filePath && !active.content.trim()) {
                    const revision = invalidateTabParse(active.id);
                    const parsed = await parseMagicMdDocumentAsync(
                        content,
                        filePath,
                    );
                    const current = get().tabs.find((tab) => tab.id === active.id);
                    if (
                        parseRevisionMap.get(active.id) !== revision ||
                        !current ||
                        current.filePath ||
                        current.content.trim()
                    ) {
                        return;
                    }
                    const nextMap = new Map(get().parsedDocumentMap);
                    nextMap.set(active.id, parsed);
                    set({
                        tabs: get().tabs.map((t) =>
                            t.id === active.id
                                ? { ...t, title, filePath, content, isDirty: false }
                                : t,
                        ),
                        parsedDocument: parsed,
                        parsedDocumentMap: nextMap,
                    });
                } else {
                    get().addTab({ title, filePath, content });
                    const { tabs: updatedTabs, activeTabId: newActiveId } = get();
                    set({
                        tabs: updatedTabs.map((t) =>
                            t.id === newActiveId ? { ...t, isDirty: false } : t,
                        ),
                    });
                }
                useRecentStore.getState().addRecentFile(filePath);
                get().setStatusMessage(tr("tabs.status.opened", { title }));
            } catch (err) {
                frontendLog.error("Failed to open file", err, { source: "system" });
                const errMsg = err instanceof Error ? err.message : String(err);
                if (
                    errMsg.includes("os error 60") ||
                    errMsg.includes("timed out") ||
                    errMsg.includes("iCloud")
                ) {
                    get().setStatusMessage(tr("tabs.status.icloudNotDownloaded"));
                } else {
                    get().setStatusMessage(tr("tabs.status.openFailed", { message: errMsg.slice(0, 80) }));
                }
            }
        },

        saveFile: async () => {
            const tab = get().getActiveTab();
            if (!tab) return;
            if (tab.kind === "document-doctor") {
                get().setStatusMessage(tr("tabs.status.doctorNoSave"), 2000);
                return;
            }

            // Pre-record so file-watcher echo is suppressed even if write
            // happens via saveFileAs (active tab has no filePath).
            if (tab.filePath) {
                _recentSaves.set(tab.filePath, Date.now());
            }

            const saved = await persistTabImpl(get, set, tab.id, {
                announce: true,
                allowSaveAs: true,
                source: "manual",
            });
            if (!saved && tab.filePath) {
                get().setStatusMessage(tr("tabs.status.saveFailed"));
            }
        },

        saveFileAs: async () => {
            const tab = get().getActiveTab();
            if (!tab) return;
            if (tab.kind === "workspace-config") {
                await get().saveWorkspace();
                return;
            }
            if (tab.kind === "document-doctor") {
                get().setStatusMessage(tr("tabs.status.doctorNoSaveAs"), 2000);
                return;
            }

            // Pre-record old filePath save (mirrors original wrapper behavior)
            if (tab.filePath) {
                _recentSaves.set(tab.filePath, Date.now());
            }

            const filePath = await showSaveDialog({
                defaultName: tab.filePath ?? tab.title,
            });
            if (!filePath) return;

            try {
                const result = await getPlatformService().engine.workspaceWriteFile({
                    path: filePath,
                    content: tab.content,
                    ...(tab.filePath ? { sourcePath: tab.filePath } : {}),
                });
                const authoritativeContent = result.content ?? tab.content;

                _recentSaves.set(filePath, Date.now());
                const title = fileNameFromPath(filePath);
                set((state) => ({
                    tabs: state.tabs.map((item) =>
                        item.id === tab.id
                            ? {
                                ...item,
                                title,
                                filePath,
                                ...(item.content === tab.content
                                    ? { content: authoritativeContent }
                                    : {}),
                                isDirty:
                                    item.content === tab.content ? false : true,
                            }
                            : item,
                    ),
                }));
                // Defensive atomicity: keep the registry's filePath in sync
                // with the new path.  The plugin-execution-store subscriber
                // also detects rename and unlinks the stale session, but
                // updating here ensures the registry never lags behind the
                // tab state for any downstream registry consumer that may
                // read it before the subscriber tick fires.
                tabSessionRegistry.updateFilePath(tab.id, filePath);
                const savedTab = get().tabs.find((item) => item.id === tab.id);
                if (savedTab) {
                    scheduleTabParse(
                        savedTab.id,
                        savedTab.content,
                        savedTab.filePath,
                        ACTIVATION_PARSE_DELAY,
                        get,
                        set,
                    );
                }
                get().setStatusMessage(tr("tabs.status.savedAs", { title }));
            } catch (err) {
                frontendLog.error("Failed to save file", err, { source: "system" });
                get().setStatusMessage(tr("tabs.status.saveAsFailed"));
            }

            // Post-record new filePath save (mirrors original wrapper behavior)
            const tabAfter = get().getActiveTab();
            if (tabAfter?.filePath) {
                _recentSaves.set(tabAfter.filePath, Date.now());
            }
        },

        markClean: () => {
            const { activeTabId } = get();
            set((state) => ({
                tabs: state.tabs.map((t) =>
                    t.id === activeTabId ? { ...t, isDirty: false } : t,
                ),
            }));
        },

        saveAllAndCloseTabs: async () => {
            const { tabs } = get();

            for (const tab of tabs) {
                if (
                    tab.isDirty &&
                    tab.filePath &&
                    !isReadOnlyWorkspaceFile(tab.filePath)
                ) {
                    try {
                        await getPlatformService().engine.workspaceWriteFile({
                            path: tab.filePath,
                            content: tab.content,
                        });
                        notifyConfigFileSaved(tab.filePath, tab.content);
                    } catch (err) {
                        frontendLog.error(`Failed to save ${tab.filePath}`, err, {
                            source: "system",
                        });
                    }
                }
            }

            for (const timer of parseTimerMap.values()) {
                clearTimeout(timer);
            }
            parseTimerMap.clear();
            parseRevisionMap.clear();
            set({
                tabs: [],
                activeTabId: "",
                parsedDocument: null,
                parsedDocumentMap: new Map(),
                panes: [],
                paneLayout: null,
                focusedPaneId: "pane-main",
            });
        },

        _handleExternalFileChange: (filePath: string) => {
            const lastSave = _recentSaves.get(filePath);
            if (lastSave && Date.now() - lastSave < SAVE_COOLDOWN_MS) {
                frontendLog.debug(
                    `[file-watcher] Ignoring — recent save cooldown for ${filePath}`,
                    undefined,
                    { source: "system" },
                );
                return;
            }

            const matchedTabs = get().tabs.filter((t) => t.filePath === filePath);
            if (matchedTabs.length === 0) return;

            const cleanTabIds = matchedTabs.filter((t) => !t.isDirty).map((t) => t.id);
            const dirtyTabIds = matchedTabs.filter((t) => t.isDirty).map((t) => t.id);

            frontendLog.debug(
                `[file-watcher] External change: ${filePath} — clean=${cleanTabIds.length} dirty=${dirtyTabIds.length}`,
                undefined,
                { source: "system" },
            );

            if (cleanTabIds.length > 0) {
                _reloadTabsContent(filePath, cleanTabIds, false);
            }

            if (dirtyTabIds.length > 0) {
                if (_pendingConfirms.has(filePath)) {
                    frontendLog.debug(
                        `[file-watcher] Ignoring — confirm already pending for ${filePath}`,
                        undefined,
                        { source: "system" },
                    );
                    return;
                }

                const title = matchedTabs[0]?.title ?? fileNameFromPath(filePath);
                _pendingConfirms.add(filePath);
                showConfirmDialog(
                    tr("tabs.confirm.externalChange.message", { title }),
                    { title: tr("tabs.confirm.externalChange.title"), destructive: true },
                )
                    .then(async (ok) => {
                        _pendingConfirms.delete(filePath);
                        if (!ok) return;
                        _reloadTabsContent(filePath, dirtyTabIds, true);
                    })
                    .catch(() => {
                        _pendingConfirms.delete(filePath);
                    });
            }
        },
    };
};

/**
 * Persist a tab's content to its file path. Equivalent to the original
 * module-level persistTab — kept inside this module so tabs-slice can
 * call it from saveFile (and so plugin-execution-store autosave keeps
 * working via the published export below).
 */
async function persistTabImpl(
    getState: () => EditorState,
    setState: (
        partial:
            | Partial<EditorState>
            | ((state: EditorState) => Partial<EditorState> | EditorState),
    ) => void,
    tabId: string,
    options: {
        announce: boolean;
        allowSaveAs: boolean;
        source: "manual" | "auto";
    },
): Promise<boolean> {
    const state = getState();
    const tab = state.tabs.find((item) => item.id === tabId);
    if (!tab) return false;

    if (tab.kind === "workspace-config") {
        if (options.source === "manual") {
            await state.persistSessionState({ persist: true });
            if (options.announce) {
                state.setStatusMessage(tr("tabs.status.workspaceConfigSaved"), 2000);
            }
            return true;
        }
        return false;
    }
    if (tab.kind === "document-doctor") {
        return false;
    }

    if (options.source === "auto" && !tab.isDirty) return false;

    if (!tab.filePath) {
        if (!options.allowSaveAs || state.activeTabId !== tabId) {
            return false;
        }
        await state.saveFileAs();
        return true;
    }

    if (isReadOnlyWorkspaceFile(tab.filePath)) {
        // Binary files and compressed external outputs are read-only in the
        // editor. Refuse to write back so we do not corrupt binary bytes or a
        // Brotli payload with plain UTF-8 tab content.
        return false;
    }

    frontendLog.debug(
        `[save] persistTab: tab=${tabId} source=${options.source} file=${tab.filePath}`,
        undefined,
        { source: "system" },
    );

    try {
        const result = await getPlatformService().engine.workspaceWriteFile({
            path: tab.filePath,
            content: tab.content,
        });
        const authoritativeContent = result.content ?? tab.content;

        notifyConfigFileSaved(tab.filePath, tab.content);
        _recentSaves.set(tab.filePath, Date.now());

        setState((current) => ({
            tabs: current.tabs.map((item) =>
                item.id === tabId
                    ? {
                        ...item,
                        ...(item.content === tab.content
                            ? { content: authoritativeContent }
                            : {}),
                        isDirty: item.content === tab.content ? false : item.isDirty,
                    }
                    : item,
            ),
        }));

        if (authoritativeContent !== tab.content) {
            const savedTab = getState().tabs.find((item) => item.id === tabId);
            if (savedTab?.content === authoritativeContent) {
                scheduleTabParse(
                    savedTab.id,
                    savedTab.content,
                    savedTab.filePath,
                    ACTIVATION_PARSE_DELAY,
                    getState,
                    setState as (partial: Partial<EditorState>) => void,
                );
            }
        }

        if (options.announce) {
            getState().setStatusMessage(tr("tabs.status.fileSaved", { title: tab.title }));
        }
        return true;
    } catch (err) {
        frontendLog.error("Failed to save file", err, { source: "system" });
        if (options.source === "manual") {
            getState().setStatusMessage(tr("tabs.status.saveFailed"));
        }
        return false;
    }
}

function notifyConfigFileSaved(filePath: string, content: string): void {
    if (typeof window === "undefined" || !filePath.includes(CONFIGS_PATH_MARKER)) {
        return;
    }
    const bundleDir = getBundleDirFromConfigFilePath(filePath);
    window.dispatchEvent(new CustomEvent(CONFIG_FILE_CHANGED_EVENT, {
        detail: {
            path: filePath,
            content,
            bundleDir: bundleDir ?? undefined,
        },
    }));
    if (!bundleDir) return;
    window.dispatchEvent(new CustomEvent(BUNDLE_REFRESH_EVENT, {
        detail: {
            filePath,
            bundleDir,
        },
    }));
}

function getBundleDirFromConfigFilePath(filePath: string): string | null {
    const index = filePath.indexOf(CONFIGS_PATH_MARKER);
    if (index < 0) return null;
    return filePath.slice(0, index + ".magic-md".length);
}
